import React, { useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	AppBar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	Divider,
	IconButton,
	Toolbar,
	Typography,
} from '@mui/material';

import useSubmitApplicationForm from '@pages/ApplicationForm/containers/ApplicationFormMain/containers/SubmitApplicationForm/hooks';

import * as environment from '@utils/environment';

function SubmitApplicationForm({
	runBackground,
	applicationId,
	formId,
	formVersion,
	needVerifyAccount,
	applicationCreatedAt,
}: {
	runBackground: boolean;
	applicationId: number;
	formId: number;
	formVersion?: number | null;
	needVerifyAccount: boolean;
	applicationCreatedAt: string;
}) {
	const { isSubmitting, handleSubmitApplicationForm, checkSTPAllowed } = useSubmitApplicationForm({
		applicationId,
		formId,
		formVersion,
		runBackground,
		applicationCreatedAt,
	});

	const isLightSpeed = environment.env.FIRM_ENVIRONMENT === 'lightspeed';
	const isStaging = environment.env.DEPLOYMENT_ENVIRONMENT === 'staging';

	const [isStpModalOpen, setIsStpModalOpen] = useState<boolean>(false);
	const [stpResult, setStpResult] = useState<ReturnType<typeof checkSTPAllowed> | null>(null);

	const handleCloseStpModal = useCallback((): void => {
		setIsStpModalOpen(false);
	}, []);

	const handleOpenStpModal = useCallback((): void => {
		const result = checkSTPAllowed();
		setStpResult(result);
		setIsStpModalOpen(true);
	}, [checkSTPAllowed]);

	const getRuleStatusLabel = (status: 'pass' | 'fail' | 'na'): string => {
		if (status === 'na') return 'N/A';
		return status === 'pass' ? 'PASS' : 'FAIL';
	};

	const getRuleStatusColor = (status: 'pass' | 'fail' | 'na'): string => {
		if (status === 'na') return '#9e9e9e';
		return status === 'pass' ? '#2e7d32' : '#d32f2f';
	};

	const getCheckStatusLabel = (status: 'yes' | 'no' | 'pass' | 'fail' | 'na'): string => {
		if (status === 'na') return 'N/A';
		if (status === 'pass') return 'PASS';
		if (status === 'fail') return 'FAIL';
		return status === 'yes' ? 'Yes' : 'No';
	};

	const getCheckStatusColor = (status: 'yes' | 'no' | 'pass' | 'fail' | 'na'): string => {
		if (status === 'na') return 'text.disabled';
		return status === 'yes' || status === 'pass' ? 'success.main' : 'error.main';
	};

	return (
		<div style={{ display: 'flex', gap: 20 }}>
			{isLightSpeed && isStaging && (
				<Button
					disableElevation
					variant="contained"
					size="medium"
					color="success"
					disabled={isSubmitting || needVerifyAccount}
					onClick={handleOpenStpModal}
					sx={{ textTransform: 'capitalize', width: 120 }}
				>
					<b>Check STP</b>
				</Button>
			)}
			<Dialog fullScreen open={isStpModalOpen} onClose={handleCloseStpModal}>
				<AppBar position="sticky" color="default" elevation={0}>
					<Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1, sm: 2 } }}>
						<Typography variant="h6" sx={{ fontSize: { xs: 18, sm: 20 } }}>
							<b>STP Check</b>
						</Typography>

						<IconButton edge="end" color="inherit" onClick={handleCloseStpModal} aria-label="close">
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>

				<Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%', maxWidth: 1200, margin: '0 auto' }}>
					<Typography variant="h4" sx={{ mb: 1, fontSize: { xs: 32, sm: 40 } }}>
						<b style={{ color: stpResult?.isAllowed === true ? '#2e7d32' : '#d32f2f' }}>
							{stpResult?.isAllowed === true ? 'PASS' : 'FAIL'}
						</b>
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: 14, sm: 16 } }}>
						{stpResult?.isAllowed === true
							? 'STP is allowed for this application.'
							: 'STP is not allowed for this application.'}
					</Typography>

					<Divider sx={{ my: 3 }} />

					<Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.25 } }}>
						{stpResult?.rules.map((rule, index) => (
							<Accordion key={`${index}-${rule.title}`} disableGutters sx={{ borderRadius: 1 }}>
								<AccordionSummary
									expandIcon={<ExpandMoreIcon />}
									sx={{
										px: { xs: 1.5, sm: 2 },
										'& .MuiAccordionSummary-content': {
											minWidth: 0,
											my: 1,
										},
									}}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											gap: { xs: 1, sm: 2 },
											width: '100%',
										}}
									>
										<Typography
											variant="body1"
											sx={{
												flex: 1,
												minWidth: 0,
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
												fontSize: { xs: 14, sm: 16 },
											}}
										>
											{`${index + 1}. ${rule.title}`}
										</Typography>
										<Typography
											variant="body1"
											sx={{
												flexShrink: 0,
												fontWeight: 700,
												color: getRuleStatusColor(rule.status),
												fontSize: { xs: 13, sm: 16 },
											}}
										>
											{getRuleStatusLabel(rule.status)}
										</Typography>
									</Box>
								</AccordionSummary>
								<AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, pb: { xs: 1.5, sm: 2 } }}>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
										{rule.checks.map((check, checkIndex) => (
											<Box
												key={`${index}-${checkIndex}-${check.title}`}
												sx={{
													display: 'flex',
													alignItems: 'center',
													justifyContent: 'space-between',
													gap: { xs: 1, sm: 2 },
												}}
											>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ fontSize: { xs: 12, sm: 14 }, minWidth: 0, wordBreak: 'break-word' }}
												>
													{check.title}
												</Typography>
												<Typography
													variant="body2"
													sx={{
														flexShrink: 0,
														fontWeight: 700,
														color: getCheckStatusColor(check.status),
														fontSize: { xs: 12, sm: 14 },
													}}
												>
													{getCheckStatusLabel(check.status)}
												</Typography>
											</Box>
										))}
									</Box>
								</AccordionDetails>
							</Accordion>
						))}
					</Box>
				</Box>
			</Dialog>
			<Button
				disableElevation
				variant="contained"
				size="medium"
				color="primary"
				disabled={isSubmitting || needVerifyAccount}
				onClick={handleSubmitApplicationForm}
				sx={{ textTransform: 'capitalize', width: 120 }}
			>
				<b>{isSubmitting ? <CircularProgress color={'info'} size={25} /> : 'Submit form'}</b>
			</Button>
		</div>
	);
}

export default SubmitApplicationForm;
